import React, {useState} from 'react';
import styled from 'styled-components';
import SGBody from 'components/Typography/SGBody';
import ModalIconTitle from 'features/POSRegisters/components/ModalContent/components/ModalTitle';
import ModalMainContentContainer from './components/ModalContentContainer';
import DataRow, {DataColumn} from 'features/POSRegisters/POSDiagram/components/DataGrid/DataRow';
import ModalTabs, {ModalTab} from './components/ModalTabs';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import ScanDetails, {ReceiptPrinterScanDetails} from 'features/POSRegisters/interfaces/ScanDetails';
import ModalSubHeader from './components/ModalSubHeader';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import SeverityEnum, {SeverityPriorityEnum} from 'features/POSRegisters/interfaces/Severity';
import TabPanel from 'components/Tabs/TabPanel';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';
import {AdaptedDeviceData} from 'features/POSRegisters/interfaces/AdaptedDeviceData';

//#region Styled Components
const StyledModalContent = styled.div``;
//#endregion Styled Components

//#region Column Definitions
const connectivityColumns: DataColumn[] = [
  {
    id: 'pingStatus',
    label: 'PING STATUS',
    width: '6rem',
    format: ({pingStatus}) => <SGBody variant={'b4'}>{pingStatus ?? 'Failure'}</SGBody>,
  },
  {
    id: 'ipAddress',
    label: 'IP Address',
    width: 'auto',
    minWidth: '130px',
  },
  {
    id: 'macAddress',
    label: 'MAC Address',
    width: 'auto',
    minWidth: '160px',
  },
  {
    id: 'vlanNumber',
    label: 'VLAN',
    width: 'auto',
    minWidth: '40px',
  },
  {
    id: 'switchId',
    label: 'Switch ID',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'switchNumber',
    label: 'Switch Number',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'portNumber',
    label: 'Port Number',
    width: 'auto',
    minWidth: '100px',
  },
];

const checkInColumns: DataColumn[] = [
  {
    id: 'printerFirstSeenDate',
    label: 'First Seen Date',
    width: 'auto',
    minWidth: '250px',
  },
  {
    id: 'printerLastSeenDate',
    label: 'Last Seen Date',
    width: 'auto',
    minWidth: '250px',
  },
];

const barcodeColumns: DataColumn[] = [
  {
    id: 'deviceName',
    label: 'Name',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'deviceId',
    label: 'Device Id',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'bus',
    label: 'Bus',
    width: 'auto',
    minWidth: '80px',
  },
  {
    id: 'model',
    label: 'Model',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'serialNumber',
    label: 'Serial Number',
    width: 'auto',
    minWidth: '130px',
  },
];

const informationHardwareColumns: DataColumn[] = [
  {
    id: 'deviceName',
    label: 'Device Name',
    width: 'auto',
    minWidth: '150px',
  },
  {
    id: 'manufacturer',
    label: 'Manufacturer',
    width: 'auto',
    minWidth: '90px',
  },
  {
    id: 'model',
    label: 'Model',
    width: 'auto',
    minWidth: '190px',
  },
  {
    id: 'serialNumber',
    label: 'Serial Number',
    width: 'auto',
    minWidth: '100px',
  },
  {
    id: 'printerId',
    label: 'Printer Id',
    width: 'auto',
    minWidth: '320px',
  },
];

const cashDrawerColumns: DataColumn[] = [
  {
    id: 'tillNumber',
    label: 'Name',
    width: 'auto',
    minWidth: '120px',
  },
  {
    id: 'signalConfiguration',
    label: 'Signal configuration',
    width: 'auto',
    minWidth: '130px',
  },
];
//#endregion Column Definitions

//#region Component
interface ModalContent {
  deviceType: DeviceType;
  adaptedDeviceData: AdaptedDeviceData<ReceiptPrinterScanDetails> | null;
}

export default function ReceiptPrinterModalContent({deviceType, adaptedDeviceData}: ModalContent) {
  const [activeTab, setActiveTab] = useState<ModalTab>(ModalTab.CONNECTIVITY);

  if (!adaptedDeviceData) {
    return <div>No device data available</div>;
  }
  const receiptPrinter = adaptedDeviceData?.receiptPrinter;

  const receiptPrinterSeverity = getPriorityAlert([receiptPrinter]);
  const receiptPrinterhasIssues =
    Number(SeverityPriorityEnum[receiptPrinterSeverity.toUpperCase() as any]) > 1;

  // const barcodeScannerSeverity = getPriorityAlert([barcodeScanner]);
  // const cashDrawerSeverity = getPriorityAlert([cashDrawer]);

  const tabs: {tab: ModalTab; severity?: SeverityEnum; isValid?: boolean}[] = [
    {
      tab: ModalTab.CONNECTIVITY,
      severity: getPriorityAlert([
        {
          scanResults: receiptPrinter.scanResults,
          alerts: getSubTypeAlerts(receiptPrinter.alerts, [
            AlertScanSubType.CONNECTIVITY,
            AlertScanSubType.CHECK_IN_STATUS,
          ]),
        },
      ]),
      isValid: receiptPrinter !== undefined,
    },
    {
      tab: ModalTab.INFORMATION,
      severity: getPriorityAlert([
        {
          scanResults: receiptPrinter.scanResults,
          alerts: getSubTypeAlerts(receiptPrinter.alerts, AlertScanSubType.HARDWARE),
        },
      ]),
      isValid: receiptPrinter !== undefined,
    },
    // {
    //   tab: ModalTab.BARCODE_SCANNER,
    //   severity: barcodeScannerSeverity,
    //   isValid: barcodeScanner !== undefined,
    // },
    // {
    //   tab: ModalTab.CASH_DRAWER,
    //   severity: cashDrawerSeverity,
    //   isValid: cashDrawer !== undefined,
    // },
  ];

  const handleTabChange = (tab: ModalTab) => {
    setActiveTab(tab);
  };

  return (
    <StyledModalContent>
      <ModalIconTitle
        deviceType={deviceType}
        severity={receiptPrinterhasIssues ? receiptPrinterSeverity : undefined}
        deviceName={adaptedDeviceData?.id}
      >
        <ModalTabs handleTabChange={handleTabChange} activeTabs={tabs} selectedTab={activeTab} />
      </ModalIconTitle>

      <ModalMainContentContainer>
        <TabPanel value={activeTab} index={ModalTab.CONNECTIVITY}>
          <ModalSubHeader>CONNECTIVITY</ModalSubHeader>
          <DataRow
            columns={connectivityColumns}
            data={receiptPrinter?.scanResults as ScanDetails}
            alerts={getSubTypeAlerts(receiptPrinter?.alerts, AlertScanSubType.CONNECTIVITY)}
          />

          <ModalSubHeader>CHECK-IN STATUS</ModalSubHeader>
          <DataRow
            columns={checkInColumns}
            data={receiptPrinter?.scanResults as ScanDetails}
            alerts={getSubTypeAlerts(receiptPrinter?.alerts, AlertScanSubType.CHECK_IN_STATUS)}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={ModalTab.INFORMATION}>
          <ModalSubHeader>HARDWARE</ModalSubHeader>
          <DataRow
            columns={informationHardwareColumns}
            data={receiptPrinter?.scanResults as ScanDetails}
            alerts={getSubTypeAlerts(receiptPrinter?.alerts, AlertScanSubType.HARDWARE)}
          />
        </TabPanel>
        {/* <TabPanel value={activeTab} index={ModalTab.BARCODE_SCANNER}>
          <ModalSubHeader>BARCODE SCANNER</ModalSubHeader>
          <DataRow
            columns={barcodeColumns}
            data={barcodeScanner?.scanResults as ScanDetails}
            alerts={barcodeScanner?.alerts}
          />
        </TabPanel>
        <TabPanel value={activeTab} index={ModalTab.CASH_DRAWER}>
          <ModalSubHeader>CASH DRAWER</ModalSubHeader>
          <DataRow
            columns={cashDrawerColumns}
            data={cashDrawer?.scanResults as ScanDetails}
            alerts={cashDrawer?.alerts}
          />
        </TabPanel> */}
      </ModalMainContentContainer>
    </StyledModalContent>
  );
}
//#endregion Component
